import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const MIN_LOADING_TIME = 500;
const STORAGE_KEY_PREFIX = 'planner_selected_courses_';

// Checkout deadline utilities
// Deadline: 11:59:59 PM ET on the day before the course start date

/**
 * Parse a date value that could be a string, Date object, or number
 * Returns { year, month, day } or null if invalid
 */
const parseDateParts = (dateValue) => {
  if (!dateValue) return null;

  try {
    let year, month, day;

    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) return null;
      year = dateValue.getFullYear();
      month = dateValue.getMonth() + 1;
      day = dateValue.getDate();
    } else if (typeof dateValue === 'string') {
      // Handle "2025-01-15" or "2025-01-15T00:00:00.000Z"
      const dateOnly = dateValue.split('T')[0];
      const parts = dateOnly.split('-');
      if (parts.length !== 3) return null;
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
    } else if (typeof dateValue === 'number') {
      const d = new Date(dateValue);
      if (isNaN(d.getTime())) return null;
      year = d.getFullYear();
      month = d.getMonth() + 1;
      day = d.getDate();
    } else {
      return null;
    }

    // Validate parsed values
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    if (year < 1900 || year > 2100) return null;
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;

    return { year, month, day };
  } catch {
    return null;
  }
};

/**
 * Get current time in ET as { year, month, day, hour, minute }
 */
const getNowInET = () => {
  const now = new Date();
  const etStr = now.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  // Format: "01/06/2025, 16:30:00"
  const [datePart, timePart] = etStr.split(', ');
  const [month, day, year] = datePart.split('/').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);
  return { year, month, day, hour, minute, second };
};

/**
 * Calculate milliseconds until checkout deadline
 * Deadline: 11:59:59 PM ET on the day before start_date
 * @param {string|Date|number} startDate - Course start date
 * @returns {number|null} - Milliseconds until deadline, or null if invalid
 */
export const getTimeUntilDeadline = (startDate) => {
  const parts = parseDateParts(startDate);
  if (!parts) return null;

  // Deadline is day before start at 23:59:59 ET
  const deadlineDay = parts.day - 1;
  const deadlineMonth = parts.month;
  const deadlineYear = parts.year;

  // Handle month boundary (if day was 1, go to previous month)
  let dlYear = deadlineYear;
  let dlMonth = deadlineMonth;
  let dlDay = deadlineDay;
  if (dlDay < 1) {
    dlMonth -= 1;
    if (dlMonth < 1) {
      dlMonth = 12;
      dlYear -= 1;
    }
    // Get last day of previous month
    dlDay = new Date(dlYear, dlMonth, 0).getDate();
  }

  // Get current time in ET
  const now = getNowInET();

  // Calculate difference in minutes
  // Deadline: dlYear/dlMonth/dlDay 23:59:59
  // Now: now.year/now.month/now.day now.hour:now.minute:now.second

  // Convert both to "minutes since epoch" for easy comparison
  // Using a simple approach: convert to Date objects and compare
  const deadlineDate = new Date(dlYear, dlMonth - 1, dlDay, 23, 59, 59);
  const nowDate = new Date(now.year, now.month - 1, now.day, now.hour, now.minute, now.second);

  const diffMs = deadlineDate.getTime() - nowDate.getTime();
  return isNaN(diffMs) ? null : diffMs;
};

/**
 * Check if checkout has expired for a course
 * @param {string|Date|number} startDate - Course start date
 * @returns {boolean} - True if deadline has passed
 */
export const isCheckoutExpired = (startDate) => {
  const ms = getTimeUntilDeadline(startDate);
  if (ms === null) return false;
  return ms <= 0;
};

/**
 * Format time until deadline as "Xd Xh" or "Xh Xm"
 * @param {string|Date|number} startDate - Course start date
 * @returns {string|null} - Formatted string, or null if expired/no date
 */
export const formatTimeUntilDeadline = (startDate) => {
  const msRemaining = getTimeUntilDeadline(startDate);
  if (msRemaining === null || msRemaining <= 0) return null;

  const totalMinutes = Math.floor(msRemaining / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  if (isNaN(days) || isNaN(hours) || isNaN(minutes)) return null;

  if (days > 0) {
    return `${days}d ${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
};

// Helper to get stored selections for a student
const getStoredSelections = (studentId) => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${studentId}`);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Helper to save selections for a student
const saveSelections = (studentId, selections) => {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${studentId}`, JSON.stringify(selections));
  } catch {
    // Ignore storage errors
  }
};

// Helper to clear selections for a student (e.g., after successful payment)
export const clearStoredSelections = (studentId) => {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${studentId}`);
  } catch {
    // Ignore storage errors
  }
};

export function useSuggestedCourses(studentId) {
  const [emails, setEmails] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  // Initialize loading as true when studentId exists to prevent content flash before fetch starts
  const [loading, setLoading] = useState(!!studentId);
  const [error, setError] = useState(null);
  const [selectedSemesterId, setSelectedSemesterId] = useState(null);

  // Initialize selections from localStorage
  const [selectedCourseIds, setSelectedCourseIds] = useState(() =>
    studentId ? getStoredSelections(studentId) : []
  );

  const fetchSuggestedCourses = useCallback(async () => {
    if (!studentId) {
      setEmails([]);
      setCourses([]);
      setSemesters([]);
      return;
    }

    const startTime = Date.now();
    try {
      setLoading(true);
      const response = await api.get(`/parent/children/${studentId}/suggested-courses`);

      const elapsed = Date.now() - startTime;
      const remaining = MIN_LOADING_TIME - elapsed;

      if (remaining > 0) {
        await new Promise(resolve => setTimeout(resolve, remaining));
      }

      setEmails(response.data.emails || []);
      setCourses(response.data.courses || []);
      setSemesters(response.data.semesters || []);

      // Set default semester to current if available
      if (response.data.semesters?.length > 0) {
        const currentSemester = response.data.semesters.find(s => s.type === 'current');
        setSelectedSemesterId(currentSemester?.id || response.data.semesters[0].id);
      }

      setError(null);
    } catch (err) {
      const elapsed = Date.now() - startTime;
      const remaining = MIN_LOADING_TIME - elapsed;

      if (remaining > 0) {
        await new Promise(resolve => setTimeout(resolve, remaining));
      }

      setError(err.response?.data?.message || 'Failed to fetch suggested courses');
      setEmails([]);
      setCourses([]);
      setSemesters([]);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchSuggestedCourses();
  }, [fetchSuggestedCourses]);

  // Load selections from localStorage when studentId changes
  useEffect(() => {
    if (studentId) {
      setSelectedCourseIds(getStoredSelections(studentId));
    } else {
      setSelectedCourseIds([]);
    }
  }, [studentId]);

  // Save selections to localStorage when they change
  useEffect(() => {
    if (studentId) {
      saveSelections(studentId, selectedCourseIds);
    }
  }, [studentId, selectedCourseIds]);

  // Filter courses by selected semester
  const filteredCourses = selectedSemesterId
    ? courses.filter(course => course.semester_id === selectedSemesterId)
    : courses;

  // Helper to sort courses by start_date (oldest first)
  const sortByStartDate = (a, b) => {
    const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
    const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
    return dateA - dateB; // Ascending order (oldest first)
  };

  // Separate into suggested (unpaid) and enrolled (paid/confirmed) courses
  // A course is "enrolled" if it's confirmed (paid or pending payment)
  // - is_paid === 2: fully paid via card/Stripe
  // - is_confirmed && is_paid === 0: pending Cash/Zelle payment
  // Note: refund_status checks temporarily disabled
  const enrolledCourses = filteredCourses
    .filter(course =>
      course.is_paid === 2 || course.is_confirmed
      // && course.refund_status !== 'full'
    )
    .sort(sortByStartDate);

  // Include expired courses (they'll be shown greyed out with "Offer Ended" badge)
  const suggestedCourses = filteredCourses
    .filter(course =>
      course.is_paid !== 2 && !course.is_confirmed
      // || course.refund_status === 'full'
    )
    .sort(sortByStartDate);

  // Auto-remove expired or enrolled courses from selection
  useEffect(() => {
    if (courses.length === 0 || selectedCourseIds.length === 0) return;

    const suggestedIds = new Set(suggestedCourses.map(c => c.student_course_id));

    const invalidSelectedIds = selectedCourseIds.filter(id => {
      const course = courses.find(c => c.student_course_id === id);
      // Remove if: course not found, checkout expired, or course is now enrolled
      return !course ||
        isCheckoutExpired(course.start_date) ||
        !suggestedIds.has(id);
    });

    if (invalidSelectedIds.length > 0) {
      setSelectedCourseIds(prev =>
        prev.filter(id => !invalidSelectedIds.includes(id))
      );
    }
  }, [courses, suggestedCourses, selectedCourseIds]);

  // Filter emails by selected semester
  const filteredEmails = selectedSemesterId
    ? emails.filter(email => email.semester_id === selectedSemesterId)
    : emails;

  // Toggle course selection
  const toggleCourseSelection = useCallback((studentCourseId) => {
    setSelectedCourseIds(prev => {
      if (prev.includes(studentCourseId)) {
        return prev.filter(id => id !== studentCourseId);
      } else {
        return [...prev, studentCourseId];
      }
    });
  }, []);

  // Select all suggested (purchasable) courses
  const selectAllCourses = useCallback(() => {
    const suggestedCourseIds = suggestedCourses.map(c => c.student_course_id);
    setSelectedCourseIds(suggestedCourseIds);
  }, [suggestedCourses]);

  // Deselect all courses
  const deselectAllCourses = useCallback(() => {
    setSelectedCourseIds([]);
  }, []);

  // Get selected courses details - preserves insertion order (not sorted)
  // Maps over selectedCourseIds to maintain the order items were added to cart
  const selectedCoursesDetails = selectedCourseIds
    .map(id => suggestedCourses.find(c => c.student_course_id === id))
    .filter(Boolean); // Remove any undefined (e.g., if course was removed from suggestions)

  // Calculate total fees
  const totalFees = selectedCoursesDetails.reduce((acc, course) => {
    return {
      tuition: acc.tuition + (parseFloat(course.tuition_fee) || 0),
      textbook: acc.textbook + (parseFloat(course.textbook_fee) || 0),
      exam: acc.exam + (parseFloat(course.exam_fee) || 0),
    };
  }, { tuition: 0, textbook: 0, exam: 0 });

  totalFees.total = totalFees.tuition + totalFees.textbook + totalFees.exam;

  return {
    emails: filteredEmails,
    courses: filteredCourses,
    suggestedCourses,
    enrolledCourses,
    allCourses: courses,
    semesters,
    loading,
    error,
    selectedSemesterId,
    setSelectedSemesterId,
    selectedCourseIds,
    toggleCourseSelection,
    selectAllCourses,
    deselectAllCourses,
    selectedCoursesDetails,
    totalFees,
    refetch: fetchSuggestedCourses,
  };
}
